<template>
    <div class="organic-score-gauge">
        <div class="gauge-header">
            <span class="gauge-label">ORGANIC SCORE</span>
            <span :class="['score-badge', scoreClass]">{{ scoreLabel }}</span>
        </div>
        <div class="gauge-container">
            <div class="gauge-bar">
                <div class="gauge-fill" :style="{ width: `${score * 100}%` }" :class="scoreClass"></div>
            </div>
            <div class="gauge-markers">
                <span class="marker" style="left: 0%">0</span>
                <span class="marker" style="left: 50%">0.5</span>
                <span class="marker" style="left: 80%">0.8</span>
                <span class="marker" style="left: 100%">1.0</span>
            </div>
        </div>
        <div class="gauge-description">
            {{ description }}
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
    score: {
        type: Number,
        default: 0,
    }
});

const scoreClass = computed(() => {
    if (props.score >= 0.8) return 'organic-high';
    if (props.score >= 0.5) return 'organic-moderate';
    return 'organic-suspicious';
});

const scoreLabel = computed(() => {
    if (props.score >= 0.8) return 'Highly Organic';
    if (props.score >= 0.5) return 'Moderate';
    return 'Suspicious';
});

const description = computed(() => {
    if (props.score >= 0.8) {
        return 'High ratio of unique traders suggests authentic trading activity.';
    }
    if (props.score >= 0.5) {
        return 'Moderate ratio - some repeat trading detected.';
    }
    return 'Low ratio may indicate wash trading or bot activity.';
});
</script>

<style scoped>
.organic-score-gauge {
    padding: 0.5rem;
    background: rgba(139, 92, 246, 0.06);
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.15));
    border-radius: 8px;
}

.gauge-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
}

.gauge-label {
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--compass-text-secondary, #7d8590);
}

.score-badge {
    font-size: 0.6rem;
    font-weight: 700;
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
    text-transform: uppercase;
}

.score-badge.organic-high {
    background: rgba(34, 197, 94, 0.2);
    color: #22c55e;
}

.score-badge.organic-moderate {
    background: rgba(251, 191, 36, 0.2);
    color: #fbbf24;
}

.score-badge.organic-suspicious {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
}

.gauge-container {
    position: relative;
    margin-bottom: 0.5rem;
}

.gauge-bar {
    height: 8px;
    background: rgba(139, 92, 246, 0.15);
    border-radius: 4px;
    overflow: hidden;
}

.gauge-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.5s ease;
}

.gauge-fill.organic-high {
    background: linear-gradient(90deg, #22c55e, #4ade80);
}

.gauge-fill.organic-moderate {
    background: linear-gradient(90deg, #f59e0b, #fbbf24);
}

.gauge-fill.organic-suspicious {
    background: linear-gradient(90deg, #dc2626, #ef4444);
}

.gauge-markers {
    display: flex;
    justify-content: space-between;
    margin-top: 0.25rem;
    position: relative;
}

.marker {
    font-size: 0.55rem;
    color: var(--compass-text-secondary, #7d8590);
    position: absolute;
    transform: translateX(-50%);
}

.gauge-description {
    font-size: 0.65rem;
    color: var(--compass-text-secondary, #7d8590);
    line-height: 1.3;
}

/* Light mode */
body:not(.dark-mode) .organic-score-gauge {
    background: rgba(139, 92, 246, 0.04);
}
</style>
